import React, { useEffect, useState } from "react";
import { ArrowDown, Calendar, Linkedin, Twitter, Send } from "lucide-react";

const HERO_TEXTURE =
  "https://images.unsplash.com/photo-1761437855598-a84c2849dc6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBibGFjayUyMGdvbGQlMjB0ZXh0dXJlJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzc2OTEzMzk4fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handle = (e) => {
      setMouse({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener("mousemove", handle);
    return () => window.removeEventListener("mousemove", handle);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden pt-28 pb-20 lg:pt-36"
    >
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.15] bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_TEXTURE})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />

      {/* Gold halo */}
      <div
        className="hero-halo"
        style={{
          top: "10%",
          right: "-15%",
          transform: `translate(${mouse.x * 0.5}px, ${mouse.y * 0.5}px)`,
        }}
      />
      <div
        className="hero-halo"
        style={{
          bottom: "-20%",
          left: "-20%",
          width: "700px",
          height: "700px",
          opacity: 0.6,
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left: editorial typography */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="animate-fade-up opacity-0" style={{ animationDelay: "100ms" }}>
              <div className="flex items-center gap-4 mb-10">
                <span className="w-14 h-px bg-gold" />
                <span className="overline">Est. 1999 · Highland, Utah</span>
              </div>
            </div>

            <h1
              className="heading-display text-[14vw] sm:text-[11vw] lg:text-[8.5vw] xl:text-[9.2rem] text-white animate-fade-up opacity-0"
              style={{ animationDelay: "250ms" }}
              data-testid="hero-headline"
            >
              <span className="block">Making</span>
              <span className="block italic text-transparent bg-clip-text bg-gradient-to-br from-[#FDE08B] via-[#D4AF37] to-[#8A6F1E]">
                Values
              </span>
              <span className="block">Profitable.</span>
            </h1>

            <p
              className="body-copy text-lg sm:text-xl text-white/50 max-w-xl mt-10 animate-fade-up opacity-0 font-light"
              style={{ animationDelay: "500ms" }}
              data-testid="hero-subline"
            >
              Hi, I'm <span className="text-gold">Ryan Best</span>. I've spent my life building
              businesses, learning through experience, and pushing ideas into action. If you value
              real-world insight and bold moves, you're in the right place.
            </p>

            <div
              className="flex flex-wrap items-center gap-4 mt-10 animate-fade-up opacity-0"
              style={{ animationDelay: "700ms" }}
            >
              <a href="#book" className="btn-primary" data-testid="hero-cta-primary">
                Book a 30-min call
                <Calendar className="w-4 h-4" />
              </a>
              <a href="#the-three" className="btn-secondary" data-testid="hero-cta-secondary">
                See The Three Babies
                <ArrowDown className="w-4 h-4" />
              </a>
            </div>

            <div
              className="flex items-center gap-6 mt-14 animate-fade-up opacity-0"
              style={{ animationDelay: "900ms" }}
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
                Connect
              </span>
              <span className="w-8 h-px bg-gold-deep" />
              <a
                href="https://www.linkedin.com/in/mrryanbest/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-gold transition-colors"
                data-testid="hero-social-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://twitter.com/MrRyanBest"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-gold transition-colors"
                data-testid="hero-social-twitter"
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="https://t.me/rybest"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-gold transition-colors"
                data-testid="hero-social-telegram"
                aria-label="Telegram"
              >
                <Send className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Right: Portrait frame */}
          <div
            className="lg:col-span-5 order-1 lg:order-2 animate-fade-up opacity-0"
            style={{ animationDelay: "400ms" }}
          >
            <div
              className="relative"
              style={{ transform: `translate(${mouse.x * -0.3}px, ${mouse.y * -0.3}px)` }}
            >
              {/* Frame corners */}
              <div className="absolute -top-3 -left-3 w-10 h-10 border-t border-l border-gold z-20" />
              <div className="absolute -bottom-3 -right-3 w-10 h-10 border-b border-r border-gold z-20" />

              <div className="relative aspect-[4/5] overflow-hidden border border-gold-deep/80 bg-ink-surface group">
                <img
                  src="https://customer-assets.emergentagent.com/job_ryan-showcase-1/artifacts/zfqc75un_image.jpeg"
                  alt="Ryan Best"
                  className="w-full h-full object-cover object-top contrast-105 saturate-105 transition-all duration-[1500ms]"
                  data-testid="hero-portrait"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://ryanbest.com/wp-content/uploads/2023/08/Ryan-Best_large-1.png";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/90">
                      Founder · Builder · Operator
                    </div>
                    <div className="heading-section text-3xl text-white mt-2">Ryan Best</div>
                  </div>
                  <div className="font-mono text-[10px] text-white/40">01 / 25</div>
                </div>
              </div>

              {/* Vertical tag */}
              <div className="hidden lg:flex absolute top-0 -left-12 h-full flex-col items-center justify-between py-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold/60 [writing-mode:vertical-rl] rotate-180">
                  Portfolio · Past · Present · Future
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom ticker */}
        <div className="mt-20 lg:mt-32 flex items-center justify-between border-t border-gold-deep/50 pt-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
            Scroll to explore
          </div>
          <div className="flex items-center gap-6 font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
            <span>STBL.io</span>
            <span className="text-gold">·</span>
            <span>2Connect.AI</span>
            <span className="text-gold">·</span>
            <span>GoldSilverCrypto</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
